import React from 'react';
import { ArrowUpRight, ArrowDownRight, EyeOff } from 'lucide-react';
import { useFinanceStore } from '../../store/useFinanceStore';
import { formatTransactionDescription } from '../../utils/transactionDescriptionFormatter';
import { getCurrencySymbol } from '../../utils/currency';
import { Tooltip } from '../common/Tooltip';
import { formatTimeUTC } from '../../utils/timezoneUtils';

export const RecentTransactions: React.FC = () => {
  const { accounts: allAccounts } = useFinanceStore();
  const allTransactions = useFinanceStore((state) => state.transactions); // Get all transactions
  
  const formatCurrency = (amount: number, currency: string) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${Math.abs(amount).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Get the 10 most recent transactions
  const recentTransactions = allTransactions
    .filter(t => !t.tags?.some(tag => tag.includes('transfer') || tag.includes('dps_transfer')))
    .sort((a, b) => {
      // Sort by created_at, but if updated_at is more recent, use that for priority
      const aLatestTime = a.updated_at ? Math.max(new Date(a.created_at).getTime(), new Date(a.updated_at).getTime()) : new Date(a.created_at).getTime();
      const bLatestTime = b.updated_at ? Math.max(new Date(b.created_at).getTime(), new Date(b.updated_at).getTime()) : new Date(b.created_at).getTime();
      return bLatestTime - aLatestTime;
    })
    .slice(0, 5);

  if (recentTransactions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">No recent transactions</p>
      </div>
    );
  }

  return (
    <div className="max-h-[400px] overflow-y-auto">
      <div className="pb-4">
        {recentTransactions.map((transaction) => {
          const account = allAccounts.find(a => a.id === transaction.account_id);
          const currency = account?.currency || 'USD';
          const isAccountHidden = account ? !account.isActive : false;
          const accountName = account?.name || 'Unknown Account';
          return (
            <div
              key={transaction.id}
              className="flex items-center justify-between p-2 sm:p-2.5 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors mt-0"
            >
              <div className="flex items-center space-x-2">
                <div className={`p-1.5 rounded-full ${transaction.type === 'income' ? 'bg-green-100' : 'bg-red-100'}`}>
                  {transaction.type === 'income' ? (
                    <ArrowDownRight className="w-4 h-4 text-green-600" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{formatTransactionDescription(transaction.description)}</p>
                  <p className="text-xs text-gray-500">
                    {formatTimeUTC(transaction.created_at, 'MMM d, h:mm a')}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm font-medium ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                  {transaction.type === 'income' ? '+' : '-'}
                  {formatCurrency(transaction.amount, currency)}
                </p>
                <div className="flex items-center justify-end gap-1">
                  <p className="text-xs text-gray-500">{accountName}</p>
                  {isAccountHidden && (
                    <Tooltip 
                      content="Account is hidden"
                      placement="top"
                    >
                      <EyeOff className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

